import React, { useState, useEffect } from 'react';import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Typography,
  Container,
  Box,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Stack,
} from '@mui/material';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const OwnerRegistration = () => {
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [userId, setUserId] = useState(''); // لتخزين user_id
  const [users, setUsers] = useState([]); // قائمة المستخدمين
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // المستخدمون غير المسندين فقط (لمنع ربط مستخدم بأكثر من مالك).
        const response = await api.get(`${apiUrl}/users-unassigned`);
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  const validateFields = () => {
    if (!firstName) {
      setError(t('please_fill_first_name'));
      return false;
    }
    if (!lastName) {
      setError(t('please_fill_last_name'));
      return false;
    }
    if (!phone) {
      setError(t('please_fill_phone')); // التأكد من أن رقم الجوال مملوء
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) {
      return;
    }

    try {
      const response = await api.post(`${apiUrl}/owners`, {
        first_name: firstName,
        last_name: lastName,
        phone,
        email,
        address,
        user_id: userId, // استخدام userId بدلاً من selectedUser
      });
      console.log(response); // استخدم response بطريقة ما، مثل طباعته في وحدة التحكم
      setSuccess(t('owner_registered_successfully'));
      resetForm();
      navigate('/owner-list');
    } catch (error) {
      const code = error?.response?.data?.code;
      setError(
        code === 'USER_ALREADY_LINKED'
          ? t('user_already_linked')
          : error?.response?.data?.error || t('error_registering_owner')
      );
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setAddress('');
    setUserId('');
    setError('');
    setSuccess('');
  };

  return (
    <Box
      className="smart-page-container"
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {t('register_owner')}
        </Typography>
        <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
          {t('register_owner_hint') || 'Create an owner profile and optionally link it to a system user account.'}
        </Typography>
      </Paper>
      <Container component="main" maxWidth="md" disableGutters>
        <Paper component="form" elevation={0} onSubmit={handleSubmit} sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
          <Stack spacing={1}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="firstName"
            label={t('first_name')}
            name="firstName"
            autoComplete="firstName"
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="lastName"
            label={t('last_name')}
            name="lastName"
            autoComplete="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="phone"
            label={t('phone')}
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="email"
            label={t('email')}
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="address"
            label={t('address')}
            name="address"
            multiline
            rows={4}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('select_user')}</InputLabel>
            <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
              {users.map((user) => (
                <MenuItem key={user.user_id} value={user.user_id}>
                  {user.first_name} {user.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 3,
              mb: 2,
            }}
          >
            <Button type="submit" variant="contained" sx={{ width: '48%', bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
              {t('register_owner')}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/owner-list')} // استبدل '/owner-list' بالمسار المناسب لزر الإلغاء
              sx={{ width: '48%' }}
            >
              {t('cancel')}
            </Button>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default OwnerRegistration;
